import { Box, Typography, Pagination, CircularProgress } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { GetIssues } from "services/dashboard";
import FlashingCard from "./FlashingCard";
import { IssueTypeEnum } from "enums/shared";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import formatTime from "utils/formatTime";

import { useRTL } from "hooks/isRTL";
import {REFETCH_INTERVAL_TIME} from "enums/shared"
import { fallbackValue } from "constants/fallbackConstant";

dayjs.extend(relativeTime);

/**
 * Displays a list of cards with pagination, loading, and error states.
 *
 * @component
 * @param {Object} props The component props.
 * @param {number} props.currentPage  The current page number.
 * @param {string} props.searchBar The search term for filtering cards.
 * @param {number} props.cardsPerPage Number of cards to display per page.
 * @param {Function} props.setCurrentPage Function to set the current page.
 * @param {Function} props.t  Translation function.
 * @returns {JSX.Element} The rendered component.
 */
function FlashingCards({
  currentPage,
  searchBar,
  cardsPerPage,
  setCurrentPage,
  selectedTypes,
  orderType,
}) {
  const { data, isLoading, isError } = useQuery({
    queryKey: [
      "issues",
      { page: currentPage, searchBar, types: selectedTypes, orderType },
    ],
    queryFn: () =>
      GetIssues(
        cardsPerPage,
        currentPage,
        searchBar,
        // selectedTypes.length > 0 ? selectedTypes.join("&Type=") : null
        selectedTypes.length > 0 ? selectedTypes : [],
        // pass selected orderType to backend for filtering
        orderType
      ),
    keepPreviousData: true,
    refetchInterval: REFETCH_INTERVAL_TIME,
    onSuccess: (response) => {
      console.log("API Response:", response);
    },
  });

  const { t, isRTL } = useRTL();

  /**
   * Formats elapsed time into hours and minutes.
   *
   * @param {string} modificationDate The modification date in ISO format.
   * @returns {string} The formatted elapsed time.
   */
  const formatcycleTime = (modificationDate) => {
    if (!modificationDate) return fallbackValue;

    const now = dayjs();
    const modified = dayjs(modificationDate);

    const diffMinutes = now.diff(modified, "minute");
    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;

    let result = "";
    if (hours > 0) result += `${hours} ${t(hours > 1 ? "hours" : "hour")} `;
    if (minutes > 0)
      result += `${minutes} ${t(minutes > 1 ? "minutes" : "minute")} `;

    return `${result.trim()} ${t("ago")}`;
  };

  /**
   * Transforms raw issue data into formatted card data.
   *
   * @param {Array<Object>} issues - The raw issue data.
   * @returns {Array<Object>} The formatted card data.
   */
  const transformIssues = (issues) => {
    return issues.map((issue) => {
      const issueType = IssueTypeEnum[issue.type] || {}; // get the issue actual name and color
      const scheduledTime =
        issue.type === 1
          ? formatTime(issue.preferredDeliveryDate[0]?.end)
          : formatTime(issue.preferredPickUpDate[0]?.start);

      const actualTime = formatTime(new Date().toISOString());
      return {
        id: issue.id,
        orderId: issue.orderId,
        orderNumber: issue.orderNumber,
        status: t(issueType.value || fallbackValue),
        statusColor: issueType.color || "#000000",
        senderName: issue.senderName || fallbackValue,
        receiverName: issue.receiverName || fallbackValue,
        cycleTime: formatcycleTime(issue.modificationDate),
        scheduledTime,
        actualTime,
        driverName: issue.driverName || fallbackValue,
        reason: issue.reason || fallbackValue,
        driverPhoneNumber: issue.driverPhoneNumber,
        senderPhoneNumber: issue.senderPhoneNumber,
        receiverPhoneNumber: issue.receiverPhoneNumber,
        rowId: issue.driverChangeRequestId,
      };
    });
  };

  if (isError) {
    return (
      <Typography sx={{ textAlign: "center", color: "text.red" }}>
        {t("errorLoadingData")}
      </Typography>
    );
  }

  const cards = transformIssues(data?.page || []);
  const totalRecords = data?.totalRecords || 0;

  //   {/* <LoadingBanner isLoading={isLoading} message={t("loading")} /> */}
  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "0.25rem 1rem 1rem 1rem",
          gap: "0.5rem",
          minHeight: "calc(100vh - 66px)",
          marginBottom: "4.5rem",
        }}
      >
        {isLoading ? (
          // loading
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "100%",
              marginTop: "2rem",
              textAlign: "center",
            }}
          >
            <CircularProgress
              color="primary"
              sx={{
                marginBottom: "1rem",
              }}
            />
            <Typography variant="body1">{t("loading")}</Typography>
          </Box>
        ) : cards.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginTop: "2rem",
              textAlign: "center",
              width: "100%",
            }}
          >
            <Typography variant="h6" sx={{ color: "text.secondary" }}>
              {t("noDataAvailable")}
            </Typography>
          </Box>
        ) : (
          cards.map((card) => (
            <FlashingCard key={card.id} card={card} sx={{ gap: "1rem" }} />
          ))
        )}
      </Box>

      {/* pagination */}
      <Box
        sx={{
          position: "sticky",
          bottom: 66,
          backgroundColor: "background.white",
          boxShadow: "0 -2px 4px rgba(0, 0, 0, 0.3)",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0.5rem 1.5rem",
          zIndex: 999,
        }}
      >
        <Typography sx={{ fontWeight: "600" }}>
          {totalRecords > 0
            ? `${(currentPage - 1) * cardsPerPage + 1} - ${Math.min(
                currentPage * cardsPerPage,
                totalRecords
              )} ${t("of")} ${totalRecords}`
            : ""}
        </Typography>
        <Pagination
          count={Math.ceil(totalRecords / cardsPerPage) || 1}
          page={currentPage}
          onChange={(event, value) => setCurrentPage(value)}
          sx={{
            "& .MuiSvgIcon-root": {
              transform: isRTL ? "rotate(180deg)" : "none",
            },
          }}
        />
      </Box>
    </>
  );
}

export default FlashingCards;
